import { inject, injectable } from 'inversify';
import { ICrudEntityAction } from '@Redux/ICrudEntityAction';
import { Draft, Map } from '@Models';
import { TYPES } from '@Types';
import { Locale } from '@Repository/locale';
import { DraftRepository, RequestDraftSuccess } from '@Repository/tool/pairing';
import { Result } from '@Redux/type';
import { v4 as uuidv4 } from 'uuid';
import { CreateDraftParams } from '@Components/draft/create/DraftValidate';
import draftSlice from '@Redux/reducers/tool/pairing/draft.slice';
import myTeamSlice from '@Redux/reducers/tool/pairing/myTeam.slice';
import opponentTeamSlice from '@Redux/reducers/tool/pairing/opponentTeam.slice';

@injectable()
export class DraftAction extends ICrudEntityAction<Draft> {
  protected fetchDataSuccess(item: Draft): { payload: Draft; type: string } {
    return draftSlice.actions.setDraft(item);
  }

  protected cacheDataSuccess(item: Draft): { payload: Draft; type: string } {
    return draftSlice.actions.setDraft(item);
  }

  protected haveError(message: string): { payload: string; type: string } {
    return draftSlice.actions.setError(message);
  }

  protected areLoading(bool: boolean): { payload: boolean; type: string } {
    return draftSlice.actions.setLoading(bool);
  }

  @inject(TYPES.Repository.DraftRepository)
  private _repository!: DraftRepository;
  @inject(TYPES.Locale) private _locale!: Locale;

  public create =
    (values: CreateDraftParams): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const draft = {
        id: uuidv4(),
        versionId: values.versionId,
        size: values.size,
        myTeam: null,
        opponentTeam: null,
        maps: values.tables
          .map((item) => item.map)
          .filter((item): item is Map => !!item),
      } satisfies Draft as Draft;

      const response = await this._repository.create(draft);

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestDraftSuccess;
      dispatch(this.fetchDataSuccess(success.payload));
      dispatch(this.areLoading(false));

      window.location.href = `/${this._locale.getlocale()}/tool/pairing/teams/${
        success.payload.myTeam!.id
      }/new`;
    };

  public getByTeam =
    (teamId: string): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const response = await this._repository.getByTeamId(teamId, {
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestDraftSuccess;
      dispatch(this.fetchDataSuccess(success.payload));
      dispatch(myTeamSlice.actions.setTeam(success.payload.myTeam!));
      dispatch(
        opponentTeamSlice.actions.setTeam(success.payload.opponentTeam!),
      );

      dispatch(this.areLoading(false));
    };
}
